import ContextVariableAccessor from '@/core/application-dictionary/components/ContextVariableAccessor';
import AccessLevelMixin from '@/core/application-dictionary/mixins/AccessLevelMixin';
import { IADField } from '@/shared/model/ad-field.model';
import { kebabCase } from 'lodash';
import pluralize from 'pluralize';
import { Component, Mixins, Vue, Watch } from 'vue-property-decorator';

const baseApiColumn: string = 'api/ad-columns';

const AdInputLookupProps = Vue.extend({
  props: {
    classNames: [Array, Object],
    disabled: Boolean,
    fieldMeta: Object,
    items: Array,
    labelFields: Array,
    lookupByField: String,
    placeholder: String,
    query: Array,
    size: String,
    tabId: String,
    tableName: String,
    value: [String, Number],
  },
});

@Component
export default class AdInputLookup extends Mixins(AccessLevelMixin, ContextVariableAccessor, AdInputLookupProps) {

  private validationRule: string = null;

  private apiEndpoint: string = null;

  private isUserTable: boolean = false;

  loading: boolean = false;
  key: number | string = null;
  lookupItems: any[] = [];
  identifiers: string[] = ['id'];

  @Watch('value')
  onValueChanged(value?: number) {
    if (this.key !== value) {
      this.key = value;
    }
  }

  onChanged(value?: number) {
    this.$emit('input', value);
    this.$emit('change', value);
  }

  async created() {
    this.key = this.value;

    if (this.fieldMeta) {
      console.log('field:',this.fieldMeta);
      this.initApiEndpoint();
      this.retrieveIdentifiers();
      this.initValidationRule();
    } else if (this.tableName) {
      const resourceName = pluralize.plural(kebabCase(this.tableName));

      this.apiEndpoint = `api/${resourceName}`;
      this.isUserTable = this.tableName === 'ad_user';

      if (this.labelFields) {
        this.identifiers = this.labelFields as string[];
      } else {
        await this.retrieveIdentifiers();
      }

      this.fetchRecords(this.key);
    }
  }

  /**
   * This method is invoked each time user typing in el-select component
   * with remote attribute enabled.
   * @param query
   */
  fetchRecords(query?: string | number) {
    this.loading = true;
    let baseQuery: string[] = ['active.equals=true'];

    if (this.fieldMeta) {
      // Append additional query from the cached validation rule query, if any.
      if (this.validationRule) {
        baseQuery.push(this.validationRule);
      }
    } else if (this.query) {
      baseQuery = [...baseQuery, ...(this.query as string[])];
    }

    if (!!query) {
      if (typeof query === 'number') {
        baseQuery.push(`id.equals=${query}`);
      } else if (this.isUserTable) {
        baseQuery.push(`userLogin.contains=${query}`);
      } else {
        const fieldName = this.lookupByField || this.identifiers[0];
        baseQuery.push(`${fieldName}.contains=${query}`);
      }
    }

    this.dynamicWindowService(this.apiEndpoint)
      .retrieve({
        criteriaQuery: this.updateCriteria(baseQuery),
        paginationQuery: {
          page: 0,
          size: 20,
          sort: this.isUserTable ? ['id'] : this.identifiers,
        },
      })
      .then(res => {
        this.lookupItems = res.data;
        this.$emit('update:items', this.lookupItems);
      })
      .finally(() => (this.loading = false));
  }

  private initApiEndpoint() {
    const field = this.fieldMeta as IADField;
    const column = field.adColumn;
    const tableName = column?.foreignKey ? column.importedTable : field.adTable.name;
    const resourceName = pluralize.plural(kebabCase(tableName));
    this.apiEndpoint = `api/${resourceName}`;
    this.isUserTable = tableName === 'ad_user';
  }

  private async initValidationRule() {
    // Parse the validation rule which is used to filter the reference key records.
    const field = this.fieldMeta as IADField;
    const column = field.adColumn;
    const overrideColumn = field.adValidationRule !== void 0 && field.adValidationRule !== null;
    const vRule = overrideColumn ? field.adValidationRule : column?.adValidationRule;

    if (vRule) {
      let referenceFilter = <string>this.getContext({
        defaultTabId: this.tabId,
        text: vRule.query,
      });

      if (referenceFilter?.includes('{select', 0)) {
        this.loading = true;

        try {
          referenceFilter = await this.parseValidationQuery({
            defaultTabId: this.tabId,
            text: referenceFilter,
          });
        } catch (err) {
          console.log('Failed initializing validation rule');
        }
      }

      console.log('Field %s validationRule: %s', field.name, referenceFilter)

      // Avoid exception when creating a new record, which will invoke
      // the backend API with a bad request.
      if (referenceFilter && !referenceFilter.includes('=null')) {
        this.validationRule = referenceFilter;
      }
    }

    this.initRelationships();
  }

  private initRelationships() {
    this.loading = true;

    const field = this.fieldMeta as IADField;
    const column = field.adColumn;
    const tableName = column?.foreignKey ? column.importedTable : field.adTable.name;
    const resourceName = pluralize.plural(kebabCase(tableName));
    const api = `api/${resourceName}`;
    const criteriaQuery: string[] = this.key ? [`id.equals=${this.key}`] : [];

    // Append additional query from the cached validation rule query, if any.
    if (this.validationRule) {
      criteriaQuery.push(this.validationRule);
    }

    this.dynamicWindowService(api)
      .retrieve({ criteriaQuery })
      .then(res => (this.lookupItems = res.data))
      .finally(() => (this.loading = false));
  }

  private async retrieveIdentifiers() {
    let baseQuery = [];

    if (this.fieldMeta) {
      const field = this.fieldMeta as IADField;
      const column = field.adColumn;
      const table = column?.foreignKey ? column.importedTable : field.adTable.id;

      baseQuery = [`${typeof table === 'string' ? 'adTableName' : 'adTableId'}.equals=${table}`];
    } else if (this.tableName) {
      baseQuery = [`adTableName.equals=${this.tableName}`];
    }

    const res = await this.dynamicWindowService(baseApiColumn)
      .retrieve({
        criteriaQuery: ['active.equals=true', 'identifier.equals=true', ...baseQuery],
      });

    if (res.data.length) {
      this.identifiers = res.data.map(item => item.name);
    }
  }

  printLabel(data: any) {
    console.log("this data.name",data.name)
    return data.name
  }
}
